import { DrawerCard, DrawerField, DrawerTitle } from "@mixitone/components";
import TickIcon from "@mixitone/icons/tick.svg";
import UserIcon from "@mixitone/icons/user.svg";
import { ApplicationView } from "@mixitone/mvc";
import { formatDate } from "@mixitone/util";
import Form from "components/Forms/Form";
import FormButtons from "components/Forms/FormButtons";
import { DrawerFormField, DrawerFormFieldset } from "components/Forms/FormRenderers/DrawerFormRenderer";
import { TournamentPlayerFormController } from "components/Forms/TournamentPlayerForm";
import { DrawerContextLink } from "components/common/DrawerContextLink";
import { RoutedDrawer } from "components/common/RoutedDrawer";
import { injectModal } from "lib/injectParams";

const EditPlayerDrawer = TournamentPlayerFormController.scope(
  ApplicationView((_props: { id: string }) => {
    const controller = TournamentPlayerFormController.use();
    const { loading, record, paymentMethod } = controller.state;

    if (loading) return <RoutedDrawer spinner={loading} />;

    const fields = controller.fields();
    const basicFields = Object.values(fields).filter((field) => field.group === "basic");
    const showPaymentInfo = record?.tournamentSession?.requiresPayment;
    const methodLabel = paymentMethod?.description || paymentMethod?.payment_type || null;

    return (
      <RoutedDrawer>
        <DrawerTitle
          icon={UserIcon}
          contextLink={
            <DrawerContextLink
              context="tournamentPlayers"
              params={{ id: controller.props.attributes.tournament_session_id }}
            />
          }
        >
          Edit player
        </DrawerTitle>
        <Form name="player">
          <div className="space-y-6">
            <DrawerFormFieldset fields={basicFields} />
            {showPaymentInfo && (
              <DrawerCard>
                {record.paid_at && (
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 rounded border border-green-300 bg-green-50 px-3 py-2.5 text-green-900">
                      <TickIcon className="mt-0.5 h-5 w-5 shrink-0 stroke-green-700" aria-hidden />
                      <div>
                        <div className="text-sm font-semibold">Paid</div>
                        <div className="text-xs text-green-800">
                          Payment received {formatDate(record.paid_at)}
                        </div>
                      </div>
                    </div>
                    {methodLabel && (
                      <DrawerField label="Method">
                        <span className="text-sm">{methodLabel}</span>
                      </DrawerField>
                    )}
                    {record.payment_reference && (
                      <DrawerField label="Reference">
                        <span className="text-sm">{record.payment_reference}</span>
                      </DrawerField>
                    )}
                  </div>
                )}
                {!record.paid_at && (
                  <div className="space-y-3">
                    <div
                      className={
                        controller.paidByEntry
                          ? "rounded border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700"
                          : "rounded border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm font-medium text-amber-950"
                      }
                    >
                      {controller.paidByEntry ? "No payment required" : "Not paid"}
                    </div>
                    {methodLabel && (
                      <DrawerField label="Method">
                        <span className="text-sm">{methodLabel}</span>
                      </DrawerField>
                    )}
                    {record.payment_reference && (
                      <DrawerField label="Reference">
                        <span className="text-sm">{record.payment_reference}</span>
                      </DrawerField>
                    )}
                  </div>
                )}
                {!controller.paidByEntry && controller.canManuallyMarkPaid && (
                  <div className="mt-3">
                    <DrawerFormField field={fields.paid} record={record} />
                  </div>
                )}
              </DrawerCard>
            )}
            <DrawerCard>
              <DrawerFormField field={fields.competitions} record={record} hideLabel />
              <DrawerFormField field={fields.confirmed} record={record} />
            </DrawerCard>
            <DrawerFormField field={fields.checkIn} record={record} hideLabel />
            {record && <FormButtons />}
          </div>
        </Form>
      </RoutedDrawer>
    );
  }),
);

const ParamWrapper = ({ id, playerId }: { id: string; playerId: string }) => {
  return <EditPlayerDrawer id={playerId} attributes={{ tournament_session_id: id }} />;
};

export default injectModal(ParamWrapper);
